import axios, { ResponseType } from 'axios';
import moment from 'moment';
import Config from './../config';
import Cookies from 'js-cookie';
import { IPaginationResourceShort } from '../props/general';

export interface IRequestFilterProps {
  key: string;
  value: string[];
}

export const refreshToken = async () => {
  try {
    // check if token is expired
    const expiry = Cookies.get('jg_admin_expiry');
    if (moment(expiry).diff(moment()) < 0) {
      let refreshToken = Cookies.get('jg_admin_refresh');
      if (!refreshToken || refreshToken === 'undefined') return;
      const result = await axios.post(
        Config.apiHostname + '/api/auth/refresh',
        {},
        {
          headers: {
            accept: 'application/json',
            authorization: refreshToken,
            'content-type': 'application/json',
          },
        }
      );

      if (result?.data?.token) {
        Cookies.set('jg_admin_token', result.data.token.access_token);
        Cookies.set('jg_admin_refresh', result.data.token.refresh_token);
        Cookies.set(
          'jg_admin_expiry',
          moment()
            .add(result.data.token.expires_in - 60, 'seconds')
            .toISOString()
        );
      }
    }
  } catch (e) {
    console.error('Error refreshing token:', e);
  }
};

const getAuthHeader = (disableToken?: boolean) => {
  if (disableToken) return undefined;
  const token = Cookies.get('jg_admin_token');
  if (!token || token === 'undefined') return undefined;
  return 'Bearer ' + token;
};

const convertPagination = (pagination: any): IPaginationResourceShort => {
  return {
    currentPage: pagination.current_page,
    total: pagination.total || 0,
    lastPage: pagination.last_page || 1,
    nextPageUrl: pagination.next_page_url,
    prevPageUrl: pagination.prev_page_url,
  };
};

const Request = {
  getNext: async (url: string) => {
    try {
      await refreshToken();
      const res = await axios.get(url, {
        headers: {
          accept: 'application/json',
          authorization: getAuthHeader(),
        },
      });
      const pagination = convertPagination(res.data.pagination);
      return { data: res.data.data, pagination };
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  get: async (api: string, disableToken?: boolean, responseType?: ResponseType) => {
    try {
      if (!disableToken) {
        await refreshToken();
      }
      let headers: any = {
        accept: 'application/json',
        authorization: getAuthHeader(disableToken),
      };

      const res = await axios.get(Config.apiHostname + api, {
        headers,
      });
      const pagination = res.data.pagination ? convertPagination(res.data.pagination) : undefined;
      if (res.data.data) {
        return pagination ? { data: res.data.data, pagination } : res.data.data;
      } else {
        return res;
      }
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  post: async (api: string, body?: any, contentType?: string, disableToken?: boolean, skipRefreshToken?: boolean) => {
    try {
      if (!skipRefreshToken) {
        await refreshToken();
      }
      const headers: any = {
        accept: 'application/json',
        authorization: getAuthHeader(disableToken),
      };

      if (contentType && !contentType.includes('multipart/form-data')) {
        headers['content-type'] = contentType;
      } else if (!contentType && !(body instanceof FormData)) {
        headers['content-type'] = 'application/json';
      }

      const res = await axios.post(Config.apiHostname + api, body, { headers });
      return res.data.data;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  download: async (api: string) => {
    try {
      await refreshToken();
      const res = await axios.get(Config.apiHostname + api, {
        headers: { authorization: getAuthHeader() },
        responseType: 'blob',
      });
      return res;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  getBlob: async (api: string, disableToken?: boolean, responseType?: ResponseType) => {
    try {
      await refreshToken();
      const res = await axios.get(Config.apiHostname + api, {
        headers: {
          'Content-Type': 'application/json',
          authorization: getAuthHeader(disableToken),
        },
      });
      return res;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  getArrayBuffer: async (api: string, disableToken?: boolean, responseType?: ResponseType) => {
    try {
      await refreshToken();
      const res = await axios.get(Config.apiHostname + api, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          authorization: getAuthHeader(disableToken),
        },
      });
      return res;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  put: async (api: string, body?: any, contentType?: string, disableToken?: boolean) => {
    try {
      await refreshToken();
      const headers: any = {
        accept: 'application/json',
        authorization: getAuthHeader(disableToken),
      };

      if (contentType && !contentType.includes('multipart/form-data')) {
        headers['content-type'] = contentType;
      } else if (!contentType && !(body instanceof FormData)) {
        headers['content-type'] = 'application/json';
      }

      const res = await axios.put(Config.apiHostname + api, body, { headers });
      return res.data.data;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  delete: async function (api: string, disableToken?: boolean, data?: any, contentType?: string) {
    try {
      await refreshToken();
      const headers: any = {
        accept: 'application/json',
        authorization: getAuthHeader(disableToken),
      };

      if (contentType && !contentType.includes('multipart/form-data')) {
        headers['content-type'] = contentType;
      } else if (!contentType && !(data instanceof FormData)) {
        headers['content-type'] = 'application/json';
      }

      const res = await axios.delete(Config.apiHostname + api, {
        headers,
        data,
      });
      return res.data.data;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
  verify: async function (url: string) {
    try {
      await refreshToken();
      await axios.post(
        url,
        {},
        {
          headers: {
            accept: 'application/json',
            authorization: getAuthHeader(),
            'content-type': 'application/json',
          },
        }
      );
      return;
    } catch (e: any) {
      throw e && e.response && e.response.data && e.response.data.error ? e.response.data.error : e.toString();
    }
  },
};

export default Request;
